export const toMillis = (value: any): number | undefined => {
  if (!value && value !== 0) {
    return undefined
  }

  if (typeof value === 'number') {
    return value < 1_000_000_000_000 ? value * 1000 : value
  }

  if (typeof value === 'string') {
    const parsed = Date.parse(value)
    return Number.isNaN(parsed) ? undefined : parsed
  }

  if (value instanceof Date) {
    return value.getTime()
  }

  if (typeof value.toMillis === 'function') {
    return value.toMillis()
  }

  if (typeof value.toDate === 'function') {
    return value.toDate().getTime()
  }

  const seconds = value.seconds ?? value._seconds
  if (typeof seconds !== 'number') {
    return undefined
  }

  const nanos = value.nanoseconds ?? value._nanoseconds
  const extraMs = typeof nanos === 'number' ? Math.floor(nanos / 1_000_000) : 0
  return seconds * 1000 + extraMs
}

export const getLatestMillis = (values: any[]): number | undefined => {
  return values.reduce<number | undefined>((latest, value) => {
    const millis = toMillis(value)
    if (!millis) {
      return latest
    }
    return !latest || millis > latest ? millis : latest
  }, undefined)
}

export const formatTimestamp = (value: any): string => {
  const millis = toMillis(value)
  if (!millis) {
    return 'not available'
  }

  const date = new Date(millis)
  const formatted = date.toLocaleString(undefined, {
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    month: 'short',
    year: 'numeric',
  })
  const ms = String(date.getMilliseconds()).padStart(3, '0')
  return formatted.replace(/(\d{1,2}:\d{2}:\d{2})/, `$1.${ms}`)
}

export const formatRelative = (ms: number | undefined, now = Date.now()): string => {
  if (!ms) {
    return 'not available'
  }

  const delta = now - ms
  const abs = Math.abs(delta)
  const suffix = delta >= 0 ? 'ago' : 'from now'
  const seconds = Math.floor(abs / 1000)

  if (seconds < 60) {
    return `${seconds}s ${suffix}`
  }

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) {
    return `${minutes}m ${suffix}`
  }

  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    return `${hours}h ${suffix}`
  }

  const days = Math.floor(hours / 24)
  return `${days}d ${suffix}`
}

export const formatDuration = (ms: number): string => {
  if (ms < 0) {
    return '0ms'
  }

  if (ms < 1000) {
    return `${Math.round(ms)}ms`
  }

  const seconds = Math.floor(ms / 1000)
  if (seconds < 60) {
    const millis = Math.round(ms % 1000)
    return millis > 0 ? `${seconds}.${String(millis).padStart(3, '0')}s` : `${seconds}s`
  }

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) {
    const rem = seconds % 60
    return rem ? `${minutes}m ${rem}s` : `${minutes}m`
  }

  const hours = Math.floor(minutes / 60)
  const remMin = minutes % 60
  if (hours < 24) {
    return remMin ? `${hours}h ${remMin}m` : `${hours}h`
  }

  const days = Math.floor(hours / 24)
  const remHours = hours % 24
  return remHours ? `${days}d ${remHours}h` : `${days}d`
}

/** Elapsed since story start: first step is `T+0ms`, later steps `T+12.347s`, etc. */
export const formatElapsedSince = (ts: number, originTs: number): string => {
  const elapsed = Math.max(0, ts - originTs)
  if (elapsed === 0) {
    return 'T+0ms'
  }
  return `T+${formatDuration(elapsed)}`
}

export const clampInterval = (
  startMs: number,
  endMs: number,
  windowStart: number,
  windowEnd: number
): {startMs: number; endMs: number} | null => {
  const start = Math.max(startMs, windowStart)
  const end = Math.min(endMs, windowEnd)
  if (end <= start) {
    return null
  }
  return {startMs: start, endMs: end}
}

export const sortEventsAscending = <T extends {ts: number}>(events: T[]): T[] =>
  [...events].sort((a, b) => a.ts - b.ts)

export const sortEventsDescending = <T extends {ts: number}>(events: T[]): T[] =>
  [...events].sort((a, b) => b.ts - a.ts)
